import { useCallback, useMemo, useState } from "react";
import { SubmittedEditDocumentState, SubmittedEditDocuments } from "../components/SubmittedEditDocuments";

export type UseSubmittedEditDocumentsResult = {
	stagedUploads: File[];
	stagedDeletions: string[];
	documentsContent: () => JSX.Element;
	documentState: SubmittedEditDocumentState;
};

export function useSubmittedEditDocuments(): UseSubmittedEditDocumentsResult {
	const [stagedUploads, setStagedUploads] = useState<File[]>([]);
	const [stagedDeletions, setStagedDeletions] = useState<string[]>([]);

	const addStagedUploads = useCallback((files: File[]) => {
		setStagedUploads((prev) => {
			const merged = [...prev, ...files];
			const uniqueByKey = new Map<string, File>();
			for (const file of merged) {
				uniqueByKey.set(`${file.name}-${file.size}-${file.lastModified}`, file);
			}
			return Array.from(uniqueByKey.values());
		});
	}, []);

	const removeStagedUpload = useCallback((file: File) => {
		setStagedUploads((prev) =>
			prev.filter(
				(f) => `${f.name}-${f.size}-${f.lastModified}` !== `${file.name}-${file.size}-${file.lastModified}`
			)
		);
	}, []);

	const addStagedDeletion = useCallback((fileName: string) => {
		setStagedDeletions((prev) => [...new Set([...prev, fileName])]);
	}, []);

	const removeStagedDeletion = useCallback((fileName: string) => {
		setStagedDeletions((prev) => prev.filter((n) => n !== fileName));
	}, []);

	const documentsContent = useCallback(() => <SubmittedEditDocuments />, []);

	const documentState = useMemo<SubmittedEditDocumentState>(
		() => ({
			stagedUploads,
			stagedDeletions,
			addStagedUploads,
			removeStagedUpload,
			addStagedDeletion,
			removeStagedDeletion
		}),
		[stagedUploads, stagedDeletions, addStagedUploads, removeStagedUpload, addStagedDeletion, removeStagedDeletion]
	);

	return { stagedUploads, stagedDeletions, documentsContent, documentState };
}
